/**
 * シーン単位のフレーム生成の調整ダイアログ (docs/features/scene-frame-generation-tuning-plan.md §4.2・§4.5)。
 *
 * - 参照画像 (静止画AIに添付する全画像: ロゴ・選択素材・商品画像・このシーンだけの追加・AI俳優) を
 *   選択素材エリア (selectedAssetsSticky) と同じ横並びのカードで示す。AI俳優はキャラクターシートではなく顔写真で示す
 *   (添付されるのはシート。2026-10-22 利用者指示)
 * - カードのクリックで添付する/しないを切り替える (ロゴ・本文メンションは固定)。このシーンだけの追加は素材パネルからの
 *   ドラッグ&ドロップのみ (素材ハブのピッカーは素材パネルと重複するため廃止)。素材パネルはヘッダー右のボタンで開く (自動では開かない)
 * - 下書き方式 (2026-10-22 利用者指示): 切替・追加・削除・リセット・追加プロンプトはダイアログ内の下書きだけを変え、
 *   「設定する」で scenes 行へ 1 回で保存する。× / Escape は下書きを捨てる (差分があるときは確認を挟む)。
 *   外側クリックでは閉じない (誤操作で捨てないため)。
 *   添付予定の一覧は下書きの overrides で BE のドライランを取り直す (保存は不要)
 * - 「設定する」の保存に失敗したら閉じずに再試行を出す。失敗した差分はキューに残るので、× で閉じるときに捨てる
 *   (残したままだと次の生成が保存失敗で止まる)
 * - 外枠は素材パネルより下の段 (modal-overlay--beside-panel)。素材パネルを開いたまま D&D を受け取るため
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSceneHandlers } from '../hooks/SceneHandlersContext';
import { useGenerationState } from '../../state/useGenerationState';
import type { CastingActor } from '../../state/types';
import { fetchSceneFrameReferencePlan } from '../../../../features/scenarios/api';
import type { SceneFrameReferenceOverrides } from '../../../../features/scenarios/types';
import {
  addAddedMaterial,
  describeReferencePlanStatus,
  normalizeReferenceOverrides,
  removeAddedMaterial,
  toggleReferencePlanItem,
  type SceneFrameReferencePlan,
  type SceneFrameReferencePlanItem,
} from '../utils/sceneFrameReferences';
import { Tooltip } from '../../../common/Tooltip';
import { ConfirmDialog } from '../../../common/ConfirmDialog';
import { useToast } from '../../../../hooks/useToast';
import { useMaterialsPanelStore } from '../../../../stores/materialsPanelStore';
import { PromptHistoryModal } from '../../../common/PromptHistoryModal';
import { PROMPT_HISTORY_KEYS } from '../../../../utils/promptHistory';
import { resolveErrorMessage } from '../../../../utils/getErrorMessage';
import {
  EMPTY_SCENE_FRAME_SETTINGS, hasSceneFrameSettings, type SceneFrameSettings,
} from '../hooks/sceneFrameSettingsQueue';
import { brandConfig } from '../../../../config/brands';
import type { Scenario } from '../types';
import gridStyles from '../../GenerationWorkspace.module.css';
import '../../../../styles/modal-common.css';
import '../../../../styles/buttons.css';
import '../../../../styles/prompt-history.css';
import '../../../../styles/form-controls.css';

const ADDITIONAL_PROMPT_MAX_LENGTH = 1000;

interface SceneFrameTuningDialogProps {
  scenario: Scenario;
  scene: Scenario['scenes'][number];
  /** 生成中は編集を止める */
  busy?: boolean;
  onClose: () => void;
}

const historyIcon = (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-block', width: 12, height: 12, marginRight: 4, verticalAlign: '-2px',
      backgroundColor: 'currentColor',
      WebkitMaskImage: 'url(/time_clock_circle.svg)', maskImage: 'url(/time_clock_circle.svg)',
      WebkitMaskSize: 'contain', maskSize: 'contain', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center', maskPosition: 'center',
    }}
  />
);

/** 設定を比較用の文字列にする (overrides の配列は順不同、追加プロンプトは前後の空白を無視) */
function settingsKey(settings: SceneFrameSettings): string {
  const o = normalizeReferenceOverrides(settings.overrides);
  return JSON.stringify({
    p: settings.additionalPrompt.trim(),
    e: [...(o?.exclude ?? [])].sort(),
    i: [...(o?.include ?? [])].sort(),
    a: o?.added ?? [],
  });
}

export function SceneFrameTuningDialog({ scenario, scene, busy = false, onClose }: SceneFrameTuningDialogProps) {
  const {
    sceneFrameSettings, collectSceneFrameReferenceCandidates, ensureAddedMaterialsResolved, forgetAddedMaterial,
    isGlobalDragging, isDraggingVideo,
  } = useSceneHandlers();
  // AI俳優の顔写真 (計画の casting 行はキャラクターシートの URL を持つが、表示は顔写真にする)
  const { selectedCasting, additionalCastings } = useGenerationState();
  const actorById = useMemo(() => {
    const map = new Map<string, CastingActor>();
    for (const actor of [...(selectedCasting ? [selectedCasting] : []), ...additionalCastings]) map.set(actor.id, actor);
    return map;
  }, [selectedCasting, additionalCastings]);
  const toast = useToast();
  const isMaterialsPanelOpen = useMaterialsPanelStore((state) => state.isOpen);
  const [dropActive, setDropActive] = useState(false);
  const {
    saveSceneFrameSettings, retrySceneFrameSettings, readSceneFrameSettings, peekSceneFrameSettings, discardSceneFrameSettings,
  } = sceneFrameSettings;

  // 素材パネルは自動では開かない (2026-10-22 利用者指示)。ヘッダー右の開閉ボタンで開く
  const toggleMaterialsPanel = useCallback(() => {
    const store = useMaterialsPanelStore.getState();
    if (store.isOpen) store.close();
    else store.open('manage');
  }, []);

  // 下書き。開いたときは保存済みの値 (前回「設定する」が失敗して残った差分があればそれを重ねる) から始める
  const [initial] = useState(() => peekSceneFrameSettings(scene));
  const [draft, setDraft] = useState<SceneFrameSettings>(() => ({ ...initial.saved, ...(initial.pending ?? {}) }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(
    initial.error ? (resolveErrorMessage(initial.error) || '保存に失敗しました') : null,
  );
  const [historyOpen, setHistoryOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const savedKey = settingsKey(readSceneFrameSettings(scene));
  const dirty = settingsKey(draft) !== savedKey;

  // 参照画像の計画 (BE ドライラン。生成と同じ関数で解決。§6.1)。下書きの overrides が変わるたびに取り直す
  const [plan, setPlan] = useState<SceneFrameReferencePlan | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const planRequestRef = useRef(0);
  // scene / scenario は保存のたびに lastResult 経由で別オブジェクトになるので、依存は id と下書きの overrides だけにする
  // (二重取得を防ぐ)。候補は最新の scene / scenario から組む
  // 候補関数も親の再レンダーで作り直されるので ref 経由で呼ぶ (依存に入れると取り直しが増える)
  const latestRef = useRef({ scenario, scene, collectSceneFrameReferenceCandidates, ensureAddedMaterialsResolved });
  latestRef.current = { scenario, scene, collectSceneFrameReferenceCandidates, ensureAddedMaterialsResolved };
  const draftOverrides = draft.overrides;
  useEffect(() => {
    const requestId = ++planRequestRef.current;
    const timer = window.setTimeout(() => {
      const {
        scenario: currentScenario, scene: currentScene,
        collectSceneFrameReferenceCandidates: collect, ensureAddedMaterialsResolved: ensureAdded,
      } = latestRef.current;
      setPlanLoading(true);
      const added = draftOverrides?.added ?? [];
      ensureAdded(added).then(() => fetchSceneFrameReferencePlan(currentScene.id, {
        candidates: collect(currentScenario, currentScene, 'first', { added }),
        overrides: draftOverrides,
        frame_type: 'first',
      })).then((res) => {
        if (planRequestRef.current !== requestId) return;
        setPlan(res.plan as unknown as SceneFrameReferencePlan);
        setPlanError(null);
      }).catch((err) => {
        if (planRequestRef.current !== requestId) return;
        setPlanError(resolveErrorMessage(err) || '添付予定の確認が取れていません');
      }).finally(() => {
        if (planRequestRef.current === requestId) setPlanLoading(false);
      });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [scene.id, draftOverrides]);

  const updateOverrides = useCallback((
    update: (current: SceneFrameReferenceOverrides | null) => SceneFrameReferenceOverrides | null,
  ) => {
    setDraft((d) => ({ ...d, overrides: update(d.overrides) }));
  }, []);

  /** このシーンだけの参照として素材を足す (素材パネルからの D&D)。画像以外は断る。下書きのみ (保存は「設定する」) */
  const handleAddMaterial = useCallback((materialId: string, contentType?: string | null) => {
    if (contentType && !contentType.startsWith('image/')) {
      toast.error('画像素材のみ追加できます');
      return;
    }
    // 追加済みなら addAddedMaterial が同じ overrides を返す
    updateOverrides((current) => addAddedMaterial(current, materialId));
    toast.success('このシーンの参照画像に追加しました (「設定する」で確定)');
  }, [toast, updateOverrides]);

  const handleRemoveAdded = useCallback((materialId: string) => {
    // キャッシュも捨てる (取得失敗が残ったまま再追加されないように)
    forgetAddedMaterial(materialId);
    updateOverrides((current) => removeAddedMaterial(current, materialId));
  }, [forgetAddedMaterial, updateOverrides]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDropActive(false);
    if (isDraggingVideo) {
      toast.error('画像素材のみ追加できます');
      return;
    }
    try {
      const raw = e.dataTransfer.getData('application/json');
      if (!raw) return;
      const data = JSON.parse(raw) as { id?: string; contentType?: string | null };
      if (!data.id) return;
      handleAddMaterial(String(data.id), data.contentType ?? null);
    } catch {
      toast.error('素材のドロップに失敗しました');
    }
  }, [handleAddMaterial, isDraggingVideo, toast]);

  const handleToggleItem = useCallback((item: SceneFrameReferencePlanItem, on: boolean) => {
    // 先にカードを切り替える (楽観更新)。順番・メイン・上限は BE のドライランの結果で補正される
    setPlan((prev) => {
      if (!prev) return prev;
      const isCasting = item.kind === 'casting';
      const delta = on ? 1 : -1;
      return {
        ...prev,
        items: prev.items.map((it) => (it.key === item.key
          ? { ...it, status: on ? 'attached' : 'excluded', reason: on ? null : 'override', order: on ? it.order : null }
          : it)),
        counts: {
          ...prev.counts,
          attached: isCasting ? prev.counts.attached : Math.max(0, prev.counts.attached + delta),
          casting: isCasting ? Math.max(0, prev.counts.casting + delta) : prev.counts.casting,
        },
      };
    });
    updateOverrides((current) => toggleReferencePlanItem(current, item, on));
  }, [updateOverrides]);

  const hasSettings = hasSceneFrameSettings(draft);
  const sceneNumber = (scenario.scenes || []).filter((s) => !s.brand_cut_position).findIndex((s) => s.id === scene.id) + 1;
  const title = sceneNumber > 0 ? `シーン${sceneNumber}のフレーム生成の調整` : 'フレーム生成の調整';

  /** 「設定する」: 下書きを 1 回で保存して閉じる。失敗したら閉じずに再試行を出す */
  const handleConfirm = useCallback(async () => {
    if (!dirty && !error) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveSceneFrameSettings(scene, {
        additionalPrompt: draft.additionalPrompt.trim(),
        overrides: normalizeReferenceOverrides(draft.overrides),
      });
      onClose();
    } catch (err) {
      setError(resolveErrorMessage(err) || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  }, [dirty, error, draft, onClose, saveSceneFrameSettings, scene]);

  const handleRetry = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await retrySceneFrameSettings(scene);
      onClose();
    } catch (err) {
      setError(resolveErrorMessage(err) || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  }, [onClose, retrySceneFrameSettings, scene]);

  /** 下書きを捨てて閉じる。保存に失敗して残った差分も捨てる (次の生成を止めないため) */
  const handleCancel = useCallback(() => {
    discardSceneFrameSettings(scene);
    onClose();
  }, [discardSceneFrameSettings, onClose, scene]);

  /** × / Escape: 設定していない差分があれば確認してから捨てる (2026-10-22 利用者指示) */
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const requestCancel = useCallback(() => {
    if (dirty || error) setDiscardConfirmOpen(true);
    else handleCancel();
  }, [dirty, error, handleCancel]);

  /** リセット: 下書きを未設定にする (保存は「設定する」) */
  const handleReset = useCallback(() => {
    setDraft({ ...EMPTY_SCENE_FRAME_SETTINGS });
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || historyOpen || saving) return;
      // 破棄の確認が出ている間の Escape は確認だけ閉じる
      if (discardConfirmOpen) {
        e.preventDefault();
        setDiscardConfirmOpen(false);
        return;
      }
      // 素材パネルが開いている間の Escape はパネル側が閉じる (ダイアログは閉じない)
      if (isMaterialsPanelOpen) return;
      e.preventDefault();
      requestCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [requestCancel, historyOpen, isMaterialsPanelOpen, saving, discardConfirmOpen]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const disabled = busy || saving;
  const dragHighlight = dropActive || (isGlobalDragging && !isDraggingVideo);
  const materialLabel = brandConfig.labels.material;
  const panelLabel = `${materialLabel}パネル`;
  const text = draft.additionalPrompt;

  // 並びは BE の計画どおり (通常画像 → AI俳優)。切替で並べ替えない (カードの位置が動かないように。2026-10-22 利用者指示)
  const orderedItems = plan?.items ?? [];
  const castingCount = plan ? plan.items.filter((it) => it.kind === 'casting').length : 0;

  return createPortal(
    // 外側クリックでは閉じない (下書きを誤って捨てないため。確定操作を持つダイアログの既存方針)
    <div className="modal-overlay modal-overlay--beside-panel">
      <div className="modal-card modal-card--lg" onClick={(e) => e.stopPropagation()} data-testid="scene-frame-tuning-dialog">
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button type="button" className="modal-close-button" onClick={requestCancel} disabled={saving} aria-label="変更を捨てて閉じる" title="変更を捨てて閉じる">×</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div className="prompt-label-row" style={{ alignItems: 'center' }}>
              <span style={{ fontSize: '0.85rem', color: 'rgba(245, 241, 230, 0.85)' }}>
                参照画像 (このシーンに添付)
                {plan && (
                  <span style={{ marginLeft: 8, fontSize: '0.75rem', color: 'rgba(245, 241, 230, 0.5)', fontVariantNumeric: 'tabular-nums' }}>
                    {plan.counts.attached}/{plan.counts.limit}
                    {plan.counts.casting > 0 && ` + AI俳優${plan.counts.casting}人`}
                  </span>
                )}
              </span>
            </div>
            {planError && (
              <div style={{ fontSize: '0.75rem', color: 'rgba(255, 180, 120, 0.9)', marginBottom: 6 }}>{planError}</div>
            )}
            {/* 選択素材エリアと同じ横並び (assetGrid / assetCard)。素材パネルからの D&D で「このシーンだけ」の参照を足す (§4.5) */}
            <div className={gridStyles.assetGridWrapper}>
              <ul
                className={`${gridStyles.assetGrid} ${dragHighlight ? gridStyles.dragOver : ''}`}
                style={{ borderRadius: 12, minHeight: 150 }}
                onDragOver={(e) => { if (!disabled) { e.preventDefault(); setDropActive(true); } }}
                onDragLeave={() => setDropActive(false)}
                onDrop={disabled ? undefined : handleDrop}
              >
                {orderedItems.map((item) => (
                  item.kind === 'casting' ? (
                    <CastingReferenceCard
                      key={item.key}
                      item={item}
                      actor={item.actor_id ? actorById.get(item.actor_id) : undefined}
                      showLetter={castingCount > 1}
                      disabled={disabled}
                      onToggle={handleToggleItem}
                    />
                  ) : (
                    <ReferenceCard key={item.key} item={item} disabled={disabled} onToggle={handleToggleItem} onRemoveAdded={handleRemoveAdded} />
                  )
                ))}
                {!plan && !planError && planLoading && (
                  <li className={gridStyles.assetCard} style={{ height: 120, justifyContent: 'center', fontSize: '0.78rem', color: 'rgba(245, 241, 230, 0.5)' }}>
                    添付予定を確認しています...
                  </li>
                )}
                {/* 末尾: 追加の受け口 (素材パネルからドロップ)。素材パネルの開閉ボタン (フローティングと同じアイコン) もここに置く */}
                <li className={gridStyles.assetCard}>
                  <div
                    className={gridStyles.assetPreview}
                    style={{
                      flexDirection: 'column', gap: 6,
                      background: dragHighlight ? 'rgba(var(--brand-primary-rgb) / 0.12)' : 'transparent',
                      border: `1px dashed ${dragHighlight ? 'rgba(var(--brand-primary-rgb) / 0.8)' : 'rgba(245, 241, 230, 0.22)'}`,
                      fontSize: '0.7rem', lineHeight: 1.5, color: 'rgba(245, 241, 230, 0.5)', textAlign: 'center', padding: 10,
                      boxSizing: 'border-box', whiteSpace: 'pre-line', transition: 'border-color 0.15s, background 0.15s',
                    }}
                  >
                    <Tooltip content={isMaterialsPanelOpen ? `${panelLabel}を閉じる` : `${panelLabel}を開く`} placement="top">
                      <button
                        type="button"
                        onClick={toggleMaterialsPanel}
                        aria-label={isMaterialsPanelOpen ? `${panelLabel}を閉じる` : `${panelLabel}を開く`}
                        aria-pressed={isMaterialsPanelOpen}
                        style={{
                          width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: 'rgba(255, 255, 255, 0.06)', border: 'none', borderRadius: 8, cursor: 'pointer', padding: 2,
                        }}
                      >
                        <img
                          src={isMaterialsPanelOpen ? '/layout-sidebar-right-collapse.svg' : '/layout-sidebar-right-expand.svg'}
                          alt=""
                          style={{ width: 20, height: 20, opacity: 0.8 }}
                        />
                      </button>
                    </Tooltip>
                    <span>{dragHighlight ? 'ここにドロップ' : `${panelLabel}から\nドロップで追加`}</span>
                  </div>
                </li>
              </ul>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 4, fontSize: '0.72rem', color: 'rgba(245, 241, 230, 0.45)' }}>
              <span>
                {plan && plan.counts.attached === 0 && plan.counts.casting === 0
                  ? '参照画像なしで生成します'
                  : 'クリックで添付する/しないを切り替えます'}
              </span>
              <span style={{ flexShrink: 0 }}>最後のフレームの生成にも適用されます</span>
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', paddingTop: 14 }}>
            <div className="prompt-label-row">
              <span style={{ fontSize: '0.85rem', color: 'rgba(245, 241, 230, 0.85)' }}>追加プロンプト</span>
              <button type="button" className="prompt-history-toggle" onClick={() => setHistoryOpen(true)} disabled={disabled}>
                {historyIcon}
                履歴
              </button>
            </div>
            <label className="form-label-common" style={{ display: 'block' }}>
            <textarea
              ref={textareaRef}
              rows={3}
              value={text}
              maxLength={ADDITIONAL_PROMPT_MAX_LENGTH}
              placeholder={'例: 俯瞰ではなく目線の高さから撮る\n背景は白で統一する'}
              disabled={disabled}
              onChange={(e) => { const value = e.target.value; setDraft((d) => ({ ...d, additionalPrompt: value })); }}
              style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.85rem', lineHeight: 1.5 }}
            />
            </label>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginTop: 6, fontSize: '0.75rem', color: 'rgba(245, 241, 230, 0.5)' }}>
              <span style={{ flex: 1, lineHeight: 1.5 }}>最初のフレームの生成プロンプトの末尾に「# 追加の指示」として足します (最後のフレームには足しません)</span>
              <span style={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{text.length}/{ADDITIONAL_PROMPT_MAX_LENGTH}</span>
            </div>
          </div>
          <div style={{ minHeight: 18, fontSize: '0.78rem', color: error ? 'rgba(255, 120, 120, 0.9)' : 'rgba(245, 241, 230, 0.5)' }}>
            {saving ? '保存中...' : error ? (
              <>
                保存できませんでした: {error}
                <button type="button" className="btn-cancel btn-cancel-sm" style={{ marginLeft: 8 }} onClick={() => { void handleRetry(); }}>再試行</button>
              </>
            ) : busy ? '生成中は変更できません' : dirty ? '変更は「設定する」を押すまで保存されません' : null}
          </div>
        </div>
        <div className="modal-actions" style={{ justifyContent: 'space-between', padding: '0 24px 20px' }}>
          <button type="button" className="btn-cancel" onClick={handleReset} disabled={disabled || !hasSettings}>
            リセット
          </button>
          {/* 下書きをまとめて保存して閉じる (2026-10-22 利用者指示で即時保存から下書き方式へ)。捨てて閉じるのは × / Escape */}
          <button type="button" className="btn-modal" onClick={() => { void handleConfirm(); }} disabled={disabled}>設定する</button>
        </div>
        <ConfirmDialog
          isOpen={discardConfirmOpen}
          title="変更を破棄しますか?"
          message="「設定する」を押していない変更があります。閉じると元の設定に戻ります。"
          confirmLabel="破棄して閉じる"
          cancelLabel="戻る"
          variant="danger"
          onConfirm={() => { setDiscardConfirmOpen(false); handleCancel(); }}
          onCancel={() => setDiscardConfirmOpen(false)}
        />
        <PromptHistoryModal
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          scope="frame_additional"
          aboveNested
          storageKey={PROMPT_HISTORY_KEYS.SCENE_FRAME_ADDITIONAL}
          onSelect={(prompt) => { setDraft((d) => ({ ...d, additionalPrompt: prompt })); setHistoryOpen(false); }}
          title="追加プロンプトの履歴"
        />
      </div>
    </div>,
    document.body,
  );
}

const KIND_LABELS: Record<SceneFrameReferencePlanItem['kind'], string> = {
  first_frame: '最初のフレーム',
  logo: 'ロゴ',
  prev_frame: '内部参照',
  mention: '本文で参照中',
  added: 'このシーンだけ',
  asset: '選択素材',
  product: '商品画像',
  casting: 'AI俳優',
};

/** ホバー時にカードの下端に出す予告 (クリックで何が起こるか) */
function HoverHint({ text }: { text: string }) {
  return (
    <div
      style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 3, pointerEvents: 'none',
        padding: '4px 6px', borderRadius: '0 0 12px 12px',
        background: 'rgba(0, 0, 0, 0.7)', color: 'rgba(245, 241, 230, 0.95)',
        fontSize: 10, fontWeight: 600, textAlign: 'center', whiteSpace: 'nowrap',
      }}
    >
      {text}
    </div>
  );
}

function hoverHintText(item: SceneFrameReferencePlanItem, fixed: boolean): string | null {
  if (item.locked) return '固定で添付';
  if (item.readonly && item.kind === 'mention') return '本文で参照中 (固定)';
  if (fixed) return null;
  return item.status === 'attached' ? 'クリックで外す' : 'クリックで添付';
}

/** 添付しない行に被せる注記 (選択素材エリアの「利用されません」と同じ見た目) */
function MutedOverlay({ text }: { text: string }) {
  return (
    <div
      style={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2, pointerEvents: 'none',
      }}
    >
      <span style={{ color: '#fff', fontSize: 11, fontWeight: 700, textShadow: '0 1px 3px rgba(0, 0, 0, 0.8)' }}>{text}</span>
    </div>
  );
}

function mutedText(item: SceneFrameReferencePlanItem): string | null {
  if (item.status === 'attached') return null;
  if (item.status === 'over_limit') return '上限超過';
  if (item.status === 'unresolved') return '取得できません';
  return '添付しない';
}

function toggleTitle(item: SceneFrameReferencePlanItem, fixed: boolean): string | undefined {
  if (item.locked) return '固定で添付します';
  if (item.readonly && item.kind === 'mention') return '本文の@メンションで参照しています (外すには本文のトークンを消してください)';
  if (fixed) return describeReferencePlanStatus(item) || undefined;
  return item.status === 'attached' ? 'クリックで添付から外す' : 'クリックで添付する';
}

/** 参照画像の 1 枚: 選択素材エリアの assetCard と同じ見た目。クリックで添付する/しない (§4.2) */
function ReferenceCard({
  item,
  disabled,
  onToggle,
  onRemoveAdded,
}: {
  item: SceneFrameReferencePlanItem;
  disabled: boolean;
  onToggle: (item: SceneFrameReferencePlanItem, on: boolean) => void;
  onRemoveAdded: (materialId: string) => void;
}) {
  // このシーンだけ追加した素材 (由来が added だけ) は ✕ で外せる
  const addedOnly = item.origins.length > 0 && item.origins.every((o) => o.kind === 'added') && !!item.material_id;
  const fixed = item.locked || item.readonly || item.status === 'unresolved';
  const muted = mutedText(item);
  const statusText = describeReferencePlanStatus(item);
  const canToggle = !fixed && !disabled;
  const [hover, setHover] = useState(false);
  const hint = hover && !disabled ? hoverHintText(item, fixed) : null;
  return (
    <li
      className={gridStyles.assetCard}
      style={{ position: 'relative', cursor: canToggle ? 'pointer' : 'default' }}
      title={toggleTitle(item, fixed)}
      onClick={canToggle ? () => onToggle(item, item.status !== 'attached') : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`scene-frame-reference-${item.key}`}
      data-status={item.status}
    >
      {muted && <MutedOverlay text={muted} />}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, ...(muted ? { opacity: 0.4, filter: 'grayscale(100%)' } : undefined) }}>
        <div className={gridStyles.assetPreviewContainer}>
          {hint && <HoverHint text={hint} />}
          {addedOnly && (
            <button
              type="button"
              className={gridStyles.assetRemove}
              disabled={disabled}
              onClick={(e) => { e.stopPropagation(); onRemoveAdded(item.material_id as string); }}
              aria-label="このシーンだけの参照から外す"
              title="このシーンだけの参照から外す"
            >
              ✕
            </button>
          )}
          <div className={gridStyles.assetPreview}>
            {item.url ? (
              <img src={item.url} alt={item.label} draggable={false} />
            ) : (
              <span style={{ fontSize: '0.7rem', color: 'rgba(245, 241, 230, 0.4)' }}>画像なし</span>
            )}
          </div>
          {item.status === 'attached' && item.order != null && (
            <span className={gridStyles.mentionBadge} title={item.is_hero ? 'メイン (最初に添付)' : `${item.order}枚目`}>
              {item.is_hero ? 'メイン' : item.order}
            </span>
          )}
        </div>
        <span className={gridStyles.selectedCastingLabel} style={{ maxWidth: 120 }} title={item.label}>{item.label}</span>
        {/* 名前が種別と同じ (名前のない商品画像など) なら種別行は出さない */}
        {(item.label !== KIND_LABELS[item.kind] || (statusText && item.status !== 'excluded')) && (
          <span style={{ fontSize: 9, color: 'rgba(245, 241, 230, 0.45)', marginTop: -4, whiteSpace: 'nowrap' }}>
            {item.label !== KIND_LABELS[item.kind] ? KIND_LABELS[item.kind] : null}
            {statusText && item.status !== 'excluded' && (item.label !== KIND_LABELS[item.kind] ? ` · ${statusText}` : statusText)}
          </span>
        )}
      </div>
    </li>
  );
}

/** AI俳優: 選択素材エリアの丸型アバター (顔写真) と同じ見た目。添付されるのはキャラクターシートだが表示は顔写真 */
function CastingReferenceCard({
  item,
  actor,
  showLetter,
  disabled,
  onToggle,
}: {
  item: SceneFrameReferencePlanItem;
  actor: CastingActor | undefined;
  showLetter: boolean;
  disabled: boolean;
  onToggle: (item: SceneFrameReferencePlanItem, on: boolean) => void;
}) {
  const fixed = item.readonly || item.status === 'unresolved';
  const muted = mutedText(item);
  const canToggle = !fixed && !disabled;
  const faceUrl = actor?.face_image_public_url || item.url;
  const name = actor?.display_name || item.label.replace(/のキャラクターシート$/, '');
  const [hover, setHover] = useState(false);
  const hint = hover && !disabled ? hoverHintText(item, fixed) : null;
  return (
    <li
      className={gridStyles.assetCard}
      style={{ position: 'relative', cursor: canToggle ? 'pointer' : 'default' }}
      title={toggleTitle(item, fixed)}
      onClick={canToggle ? () => onToggle(item, item.status !== 'attached') : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`scene-frame-reference-${item.key}`}
      data-status={item.status}
    >
      {muted && <MutedOverlay text={muted} />}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, ...(muted ? { opacity: 0.4, filter: 'grayscale(100%)' } : undefined) }}>
        <div className={gridStyles.selectedCastingAsset} style={{ width: 120, position: 'relative' }}>
          {hint && <HoverHint text={hint} />}
          <div className={gridStyles.selectedCastingImageRound}>
            {faceUrl ? (
              <img src={faceUrl} alt={name} draggable={false} />
            ) : (
              <div className={gridStyles.selectedCastingPlaceholder} />
            )}
          </div>
          {showLetter && item.status === 'attached' && item.order != null && (
            <span className={gridStyles.selectedCastingOrderBadge} style={{ right: 28 }}>{String.fromCharCode(64 + item.order)}</span>
          )}
          <span className={gridStyles.selectedCastingLabel} title={name}>{name}</span>
        </div>
        <span style={{ fontSize: 9, color: 'rgba(245, 241, 230, 0.45)', marginTop: -4, whiteSpace: 'nowrap' }}>{KIND_LABELS.casting}</span>
      </div>
    </li>
  );
}
